import React, { useState, useEffect } from "react";
import {
  ContentLayout,
  ColumnLayout,
  SpaceBetween,
  Header,
  FormField,
  Input,
  Toggle,
  AttributeEditor,
  Select,
  FileUpload,
  Button,
  KeyValuePairs,
  CopyToClipboard,
  Spinner,
} from "@cloudscape-design/components";
import QRCode from "react-qr-code";
import { useDebounce } from "use-debounce";
import { useTranslation } from "react-i18next";
import API from "../common/api";
import BaseAppLayout from "../components/BaseAppLayout";
import FullPageLoading from "../components/FullPageLoading";

const baseQRURL = `${window.location.origin}/policy_m?data=`;

// Appends a per-generation cache-busting param so a fresh QR code URL is never
// served a stale cached page by an intermediate proxy/device that ignores
// the origin's Cache-Control headers.
const buildQRUrl = (policyId, idx, cacheBuster) =>
  `${baseQRURL}${policyId}_${idx}&_=${cacheBuster}`;

const allOptions = [
  { label: "Schedule", value: 1 },
  // { label: "Debit Note", value: 2 },
];

const policyRegex = /^\d{4}\/\d{2}\/\d{2}\/\d+(?:\/\d+)?$/;

function QRUpload() {
  const { t } = useTranslation();
  const [isPageLoading, setIsPageLoading] = useState(false);
  const [policesInput] = useState("");        // kept for reference
  const [policyValid, setPolicyValid] = useState(null);
  const [isQRLoading, setIsQRLoading] = useState(false);
  const [isValidExists, setIsValidExists] = useState(false);
  const [policyNumber, setPolicyNumber] = useState(undefined);
  const [idxNumber, setIdxNumber] = useState(0);
  const [checked, setChecked] = useState(false);
  const [qRStampDocument, setQRStampDocument] = useState([]);
  const [value] = useState([]);             // kept for future use
  const [items, setItems] = useState([{ key: "", value: [], type: null }]);
  // Unique per generated QR so downstream network/device caches never reuse a stale page for a new code
  const [qrCacheBuster, setQrCacheBuster] = useState(Date.now());

  const [debouncedPolicyNumber] = useDebounce(policyNumber, 800);

  useEffect(() => {
    if (debouncedPolicyNumber === undefined) return;
    setQRStampDocument([]);
    extractAndValidatePolicies(debouncedPolicyNumber);
  }, [debouncedPolicyNumber]);

  const extractAndValidatePolicies = (val) => {
    if (!val || !policyRegex.test(val)) return;
    validateActivePolicies(val);
  };

  const validateActivePolicies = (policy_no) => {
    setPolicyValid(undefined);
    setIsQRLoading(true);
    API.get(`qr/get_pol_sys_id?pol_no=${policy_no}`)
      .then((response) => {
        if (response.data.operation === true && response.data.data?.pol_sys_id) {
          setPolicyValid(response.data.data.pol_sys_id);
          setQrCacheBuster(Date.now());
          setIsValidExists(true);
        } else {
          setPolicyValid(undefined);
          setIsValidExists(false);
        }
      })
      .catch(() => {
        setPolicyValid(undefined);
        setIsValidExists(false);
      })
      .finally(() => setIsQRLoading(false));
  };

  const getAvailableOptions = (currentItem) => {
    const usedTypes = items
      .filter((item) => item.type && item !== currentItem)
      .map((item) => item.type.value);
    return allOptions.filter((opt) => !usedTypes.includes(opt.value));
  };

  const fileUpload = () => {
    const formData = new FormData();
    formData.append("pol_no", policyNumber);
    formData.append("pol_end_idx_no", idxNumber);
    items.forEach((i) => {
      if (i.type && i.value[0]) {
        formData.append(i.type.label, i.value[0]);
      }
    });
    setIsPageLoading(true);
    API.post("qr/file_upload", formData, {
      headers: { "Content-Type": "multipart/form-data" },
    })
      .then((response) => {
        if (response.data.operation === true) {
          alert(t("File Uploaded."));
        }
      })
      .catch((err) => console.error(err))
      .finally(() => setIsPageLoading(false));
  };

  const generateDoc = async () => {
    const formData = new FormData();
    formData.append("file", qRStampDocument[0]);
    formData.append("url", buildQRUrl(policyValid, idxNumber, qrCacheBuster));
    formData.append("policy_id", policyValid);
    formData.append("idx", String(idxNumber));
    setIsQRLoading(true);
    setIsPageLoading(true);
    try {
      const response = await API.post("qr/generate_qr_document", formData, {
        headers: { "Content-Type": "multipart/form-data" },
        responseType: "blob",
      });
      const href = URL.createObjectURL(response.data);
      const link = document.createElement("a");
      link.href = href;
      link.setAttribute(
        "download",
        qRStampDocument[0].name.replace(".docx", "_QR.docx")
      );
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(href);
    } catch (err) {
      console.error(err);
    } finally {
      setIsQRLoading(false);
      setIsPageLoading(false);
    }
  };

  const isUnlocked = policyValid && parseInt(idxNumber) > -1;

  const fileUploadI18nStrings = {
    uploadButtonText: (e) => (e ? t("Choose files") : t("Choose file")),
    dropzoneText: (e) =>
      e ? t("Drop files to upload") : t("Drop file to upload"),
    removeFileAriaLabel: (e) => t("Remove file", { index: e + 1 }),
    limitShowFewer: t("Show fewer files"),
    limitShowMore: t("Show more files"),
    errorIconAriaLabel: t("Error"),
  };

  return (
    <>
      <FullPageLoading isLoading={isPageLoading} />
      <BaseAppLayout />
      <ContentLayout header={<Header variant="h1">{t("QR Upload")}</Header>}>
        <ColumnLayout columns={2} variant="text-grid">

          {/* LEFT: QR Upload */}
          <SpaceBetween size="l">
            <Header variant="h2">{t("QR Upload")}</Header>

            <FormField label={t("Policy Number")}>
              <Input
                value={policyNumber || ""}
                onChange={({ detail }) => setPolicyNumber(detail.value)}
                placeholder={t("policy_number_placeholder")}
              />
            </FormField>

            <FormField label={t("IDX")}>
              <Input
                type="number"
                value={String(idxNumber)}
                onChange={({ detail }) => setIdxNumber(detail.value)}
              />
            </FormField>

            {isQRLoading && <Spinner />}

            {isUnlocked && (
              <SpaceBetween size="m">
                <Header variant="h3">{t("Document Upload")}</Header>
                <AttributeEditor
                  onAddButtonClick={() =>
                    setItems([...items, { key: "", value: [], type: null }])
                  }
                  onRemoveButtonClick={({ detail: { itemIndex } }) => {
                    const updated = [...items];
                    updated.splice(itemIndex, 1);
                    setItems(updated);
                  }}
                  items={items}
                  addButtonText={t("Add Document")}
                  definition={[
                    {
                      label: t("Document Type"),
                      control: (item, itemIndex) => (
                        <Select
                          selectedOption={item.type}
                          options={getAvailableOptions(item)}
                          onChange={({ detail }) => {
                            const updated = [...items];
                            updated[itemIndex] = {
                              ...updated[itemIndex],
                              type: detail.selectedOption,
                            };
                            setItems(updated);
                          }}
                          placeholder={t("Select type")}
                        />
                      ),
                    },
                    {
                      label: t("File"),
                      control: (item, itemIndex) => (
                        <FileUpload
                          value={item.value}
                          onChange={({ detail }) => {
                            const updated = [...items];
                            updated[itemIndex] = {
                              ...updated[itemIndex],
                              value: detail.value,
                            };
                            setItems(updated);
                          }}
                          i18nStrings={fileUploadI18nStrings}
                        />
                      ),
                    },
                    {
                      label: t("Remarks"),
                      control: (item, itemIndex) => (
                        <Input
                          value={item.key}
                          onChange={({ detail }) => {
                            const updated = [...items];
                            updated[itemIndex] = {
                              ...updated[itemIndex],
                              key: detail.value,
                            };
                            setItems(updated);
                          }}
                        />
                      ),
                    },
                  ]}
                />
                <Button variant="primary" onClick={fileUpload}>
                  {t("Upload")}
                </Button>
              </SpaceBetween>
            )}
          </SpaceBetween>

          {/* RIGHT: QR Generate */}
          <SpaceBetween size="l">
            <Header variant="h2">{t("QR Generate")}</Header>

            <Toggle
              checked={checked}
              disabled={isQRLoading || !isUnlocked}
              onChange={({ detail }) => setChecked(detail.checked)}
            >
              {t("Generate QR on Policy")}
            </Toggle>

            {checked && isUnlocked && (
              <SpaceBetween size="m">
                <KeyValuePairs
                  items={[
                    {
                      label: t("QR Image"),
                      value: (
                        <div style={{ maxWidth: 128 }}>
                          <QRCode
                            size={10}
                            style={{
                              height: "auto",
                              maxWidth: "100%",
                              width: "100%",
                            }}
                            value={buildQRUrl(policyValid, idxNumber, qrCacheBuster)}
                            viewBox="0 0 256 256"
                          />
                        </div>
                      ),
                    },
                    {
                      label: t("QR Value"),
                      value: (
                        <CopyToClipboard
                          copyButtonAriaLabel={t("Copy QR URL")}
                          copyErrorText={t("Failed to copy")}
                          copySuccessText={t("Copied")}
                          textToCopy={buildQRUrl(policyValid, idxNumber, qrCacheBuster)}
                        />
                      ),
                    },
                  ]}
                />

                <FormField label={t("Stamp Document (.docx)")}>
                  <FileUpload
                    value={qRStampDocument}
                    onChange={({ detail }) => setQRStampDocument(detail.value)}
                    accept=".docx"
                    i18nStrings={fileUploadI18nStrings}
                  />
                </FormField>

                <Button
                  variant="primary"
                  onClick={generateDoc}
                  disabled={
                    !(
                      policyValid &&
                      parseInt(idxNumber) > -1 &&
                      qRStampDocument.length > 0
                    )
                  }
                >
                  {t("Stamp Policy Document")}
                </Button>
              </SpaceBetween>
            )}
          </SpaceBetween>

        </ColumnLayout>
      </ContentLayout>
    </>
  );
}

export default QRUpload;
